import { grpcClientFactory } from '../../grpcClientFactory';
// Proto LayerProcessingState enum values (see landscope-map-viewer.proto).
const LAYER_STATE_PROCESSING = 1;
const LAYER_STATE_FAILED = 3;
// Map proto LayerStatus objects to plain DTOs. Only processing/failed states
// are meaningful to the client (completed layers surface as artifacts); any
// other state is dropped.
export function mapLayerStatuses(protoList) {
    const out = [];
    for (const ls of protoList || []) {
        const stateNum = ls.getState();
        let state;
        if (stateNum === LAYER_STATE_PROCESSING) {
            state = 'processing';
        }
        else if (stateNum === LAYER_STATE_FAILED) {
            state = 'failed';
        }
        else {
            continue;
        }
        out.push({
            layerName: ls.getLayerName(),
            state,
            folderIds: ls.getFolderIdsList() || [],
            userRetryCount: ls.getUserRetryCount(),
            retryAvailable: ls.getRetryAvailable(),
        });
    }
    return out;
}
class StructureService {
    createAuthenticatedClient() {
        return grpcClientFactory.createAuthenticatedUserProjectsClient({ enableCompression: true });
    }
    async getStructure(projectId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to get project structure');
        }
        return new Promise((resolve, reject) => {
            try {
                const GetProjectStructureRequest = proto.landscope.mapviewer.v1.GetProjectStructureRequest;
                const grpcRequest = new GetProjectStructureRequest();
                grpcRequest.setProjectId(projectId);
                const authenticatedClient = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                authenticatedClient.getProjectStructure(grpcRequest, metadata, (err, response) => {
                    if (err) {
                        reject(new Error(`Failed to get project structure: ${err.code} ${err.message}`));
                        return;
                    }
                    if (response) {
                        const folders = response.getFoldersList() || [];
                        const artifacts = response.getArtifactsList() || [];
                        const layerStatuses = mapLayerStatuses(response.getLayerStatusesList());
                        resolve({ folders, artifacts, layerStatuses });
                    }
                    else {
                        reject(new Error('No response received from server'));
                    }
                });
            }
            catch (error) {
                reject(new Error(`Failed to create get project structure request: ${error}`));
            }
        });
    }
    async getArtifact(artifactId) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to get artifact');
        }
        return new Promise((resolve, reject) => {
            try {
                const GetArtifactRequest = proto.landscope.mapviewer.v1.GetArtifactRequest;
                const grpcRequest = new GetArtifactRequest();
                grpcRequest.setArtifactId(artifactId);
                const authenticatedClient = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                authenticatedClient.getArtifact(grpcRequest, metadata, (err, response) => {
                    if (err) {
                        reject(new Error(`Failed to get artifact: ${err.code} ${err.message}`));
                        return;
                    }
                    if (response) {
                        resolve(response);
                    }
                    else {
                        reject(new Error('No response received from server'));
                    }
                });
            }
            catch (error) {
                reject(new Error(`Failed to create get artifact request: ${error}`));
            }
        });
    }
    // Engagement analytics: report that the caller displayed one artifact of a
    // project, by object key; the server derives the layer. Callers treat this
    // as fire-and-forget.
    async recordLayerOpened(projectId, objectKey) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to record a layer open');
        }
        return new Promise((resolve, reject) => {
            try {
                const RecordLayerOpenedRequest = proto.landscope.mapviewer.v1.RecordLayerOpenedRequest;
                const grpcRequest = new RecordLayerOpenedRequest();
                grpcRequest.setProjectId(projectId);
                grpcRequest.setObjectKey(objectKey);
                const authenticatedClient = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                authenticatedClient.recordLayerOpened(grpcRequest, metadata, (err) => {
                    if (err) {
                        reject(new Error(`Failed to record layer open: ${err.code} ${err.message}`));
                        return;
                    }
                    resolve();
                });
            }
            catch (error) {
                reject(new Error(`Failed to create record layer open request: ${error}`));
            }
        });
    }
    // Owner-triggered retry of one or more failed layers. Guards (failed-only,
    // attempt cap, cooldown) are enforced server-side; rejected layers come back
    // in the result with a reason.
    async retryLayerProcessing(projectId, layerNames) {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken) {
            throw new Error('User must be authenticated to retry layer processing');
        }
        return new Promise((resolve, reject) => {
            try {
                const RetryLayerProcessingRequest = proto.landscope.mapviewer.v1.RetryLayerProcessingRequest;
                const grpcRequest = new RetryLayerProcessingRequest();
                grpcRequest.setProjectId(projectId);
                grpcRequest.setLayerNamesList(layerNames);
                const authenticatedClient = this.createAuthenticatedClient();
                const metadata = { 'authorization': `Bearer ${sessionId}|${sessionToken}` };
                authenticatedClient.retryLayerProcessing(grpcRequest, metadata, (err, response) => {
                    if (err) {
                        reject(new Error(`Failed to retry layer processing: ${err.code} ${err.message}`));
                        return;
                    }
                    if (response) {
                        resolve({
                            success: response.getSuccess(),
                            message: response.getMessage(),
                            layersQueued: response.getLayersQueuedList() || [],
                            rejected: (response.getRejectedList() || []).map((r) => ({
                                layerName: r.getLayerName(),
                                reason: r.getReason(),
                            })),
                        });
                    }
                    else {
                        reject(new Error('No response received from server'));
                    }
                });
            }
            catch (error) {
                reject(new Error(`Failed to create retry layer processing request: ${error}`));
            }
        });
    }
}
export const structureService = new StructureService();
